import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { useSelectedLocation } from './LocationContext';
import { reportTz, tzDayRangeUtc, tzToday } from './tzUtils';

// Card payments arrive under several paymentType values depending on the
// channel — same family mapping as SalesReports.js.
const CARD_TYPES = ['card', 'terminal', 'tap-to-pay', 'stripe'];

const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

/**
 * Day Close Report (Z-report) — Square/Toast-style end-of-day close-out.
 * Location scope comes from the page-level Locations selector (multi-select
 * chips at the top of Sales Reports): with ONE location it renders the
 * classic single-location close-out; with SEVERAL it renders a comparison
 * table (one column per location + a combined TOTAL column) so owners can
 * see both per-site detail and consolidated numbers. With no selection it
 * falls back to the working location from the top bar (legacy behavior).
 */
const DayCloseReport = ({ locations = [], selectedLocationIds = [] }) => {
  const locationCtx = useSelectedLocation();
  const workingLocation = locationCtx?.selectedLocation || null;

  // Default = the WORKING LOCATION's local today — a bare UTC toISOString
  // rolled the picker to tomorrow every night after ~5pm PT (seen in prod).
  const [date, setDate] = useState(() => tzToday(reportTz(workingLocation)));
  const [salesByLoc, setSalesByLoc] = useState({}); // { [locationId]: sales[] }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Effective location list: the page selection wins; otherwise the working
  // location from the home picker (so the standalone usage keeps working).
  const effectiveLocations = useMemo(() => {
    if (selectedLocationIds.length > 0) {
      return locations.filter((l) => selectedLocationIds.includes(l.id));
    }
    return workingLocation ? [workingLocation] : [];
  }, [selectedLocationIds, locations, workingLocation]);

  const load = async () => {
    const user = getAuth().currentUser;
    if (!user || effectiveLocations.length === 0) return;
    setLoading(true);
    setError('');
    try {
      const db = getFirestore();
      const results = await Promise.all(
        effectiveLocations.map(async (loc) => {
          // The day boundaries follow EACH location's own timezone (never the
          // consultant's device — the Vegas/Paris rule).
          const [start, end] = tzDayRangeUtc(date, reportTz(loc));
          const q = query(
            collection(db, 'users', user.uid, 'sales'),
            where('locationId', '==', loc.id),
            where('orderDate', '>=', Timestamp.fromDate(start)),
            where('orderDate', '<=', Timestamp.fromDate(end)),
            orderBy('orderDate', 'asc')
          );
          const snap = await getDocs(q);
          return [loc.id, snap.docs.map((d) => ({ id: d.id, ...d.data() }))];
        })
      );
      setSalesByLoc(Object.fromEntries(results));
    } catch (e) {
      console.error('[DayCloseReport] load error:', e);
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedLocationIds.join(','), workingLocation?.id]);

  const reportFor = (sales) => {
    const r = {
      gross: 0, discounts: 0, refunds: 0, net: 0, orders: 0, items: 0,
      canceled: 0, tips: 0, cashTips: 0, cardTips: 0, fees: 0, stripeFees: 0, platformFees: 0,
      cash: { orders: 0, amount: 0 }, card: { orders: 0, amount: 0 },
      open: { orders: 0, amount: 0 }, other: { orders: 0, amount: 0 },
      channels: {},
    };
    // Refund docs are NEGATIVE mirror sales (type:'refund', orderDate = the
    // refund day). Counting them as regular sales double-deducts (gross went
    // down AND the refunds line went up). The original sale already carries
    // refundedAmount, so refund docs are EXCLUDED from the base math and
    // handled separately below.
    const refundDocs = sales.filter((x) => x.type === 'refund');
    const saleIds = new Set(sales.filter((x) => x.type !== 'refund').map((x) => x.id));

    for (const s of sales) {
      if (s.type === 'refund') continue; // handled after the loop
      const status = (s.orderStatus || s.status || 'completed').toLowerCase();
      if (status === 'canceled' || status === 'cancelled') { r.canceled++; continue; }
      const total = s.total || 0;
      const discount = s.discount || 0;
      const tip = s.tip || 0;
      r.gross += total + discount;           // pre-discount merchandise+tax+tip
      r.discounts += discount;
      r.refunds += (s.refundedAmount || 0);
      r.net += total - (s.refundedAmount || 0);
      r.orders++;
      r.items += (s.itemsQty || 0);
      r.tips += tip;
      r.fees += (s.convenienceFee || 0); // processing/convenience fee collected
      r.stripeFees += (s.stripeFee || 0); // what STRIPE charged the merchant
      r.platformFees += (s.applicationFeeAmount || 0); // FodyFee platform fee (online destination charges)

      const pt = (s.paymentType || s.paymentMethod || 'cash').toString().toLowerCase();
      const family = CARD_TYPES.includes(pt) ? 'card' : pt === 'cash' ? 'cash' : pt === 'define_later' ? 'open' : 'other';
      r[family].orders++;
      r[family].amount += total;
      if (family === 'cash') r.cashTips += tip;
      if (family === 'card') r.cardTips += tip;

      const chRaw = (s.salesChannel || 'other').toString().toLowerCase();
      const ch = chRaw === 'pos' ? 'POS' : chRaw === 'kiosk' ? 'Kiosk' : chRaw === 'online orders' ? 'Online' : 'Other';
      if (!r.channels[ch]) r.channels[ch] = { orders: 0, amount: 0 };
      r.channels[ch].orders++;
      r.channels[ch].amount += total;
    }
    // Cross-day refunds: the original order is NOT in this day's sales, so
    // without this the refund would eat into today's unrelated orders. Re-add
    // the refunded amount to gross AND refunds (net impact zero). Same-day
    // refunds skip this — the original order is already in today's gross.
    for (const rd of refundDocs) {
      const amt = rd.refundedAmount || Math.abs(rd.total || 0);
      if (!amt) continue;
      if (rd.originalSaleId && saleIds.has(rd.originalSaleId)) continue;
      r.gross += amt;
      r.refunds += amt;
    }

    // Net Sales = merchandise the merchant actually keeps: charge totals minus
    // refunds AND minus every fee line (convenience pass-through collected,
    // Stripe's processing cost, and the platform fee on online orders).
    r.net -= (r.fees + r.stripeFees + r.platformFees);
    r.avgTicket = r.orders > 0 ? r.net / r.orders : 0;
    return r;
  };

  // One report per location + the combined one (all sales merged).
  const perLocation = useMemo(() => {
    return effectiveLocations.map((loc) => ({
      loc,
      report: reportFor(salesByLoc[loc.id] || []),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocations, salesByLoc]);

  const combined = useMemo(
    () => reportFor(Object.values(salesByLoc).flat()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salesByLoc]
  );

  if (effectiveLocations.length === 0) {
    return (
      <div className="card mb-4">
        <div className="card-body text-muted" style={{ fontSize: 14 }}>
          Select a working location (top bar) or pick locations above to see the day-close report.
        </div>
      </div>
    );
  }

  const isMulti = effectiveLocations.length > 1;
  const dateLabel = new Date(`${date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const generatedAt = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const locName = isMulti
    ? `${effectiveLocations.length} locations (combined)`
    : (effectiveLocations[0].companyName || effectiveLocations[0].locationName || 'Location');

  const Row = ({ label, value, bold, muted, indent }) => (
    <div className="d-flex justify-content-between" style={{ padding: '6px 0', borderBottom: '1px dashed #e5e7eb', fontSize: 14 }}>
      <span style={{ color: muted ? '#6b7280' : '#1f2937', fontWeight: bold ? 800 : 500, paddingLeft: indent ? 16 : 0 }}>{label}</span>
      <span style={{ color: muted ? '#6b7280' : '#111827', fontWeight: bold ? 800 : 600 }}>{value}</span>
    </div>
  );

  const SectionTitle = ({ children }) => (
    <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: '#9ca3af', marginTop: 18, marginBottom: 6 }}>{children}</div>
  );

  // ── Multi-location comparison table ──────────────────────────────
  const METRIC_ROWS = [
    { section: 'Sales Summary' },
    { label: 'Gross Sales', get: (r) => money(r.gross) },
    { label: 'Discounts', get: (r) => `− ${money(r.discounts)}`, muted: true },
    { label: 'Refunds', get: (r) => `− ${money(r.refunds)}`, muted: true },
    { label: 'Processing fees (collected)', get: (r) => `− ${money(r.fees)}`, muted: true, hideIfZero: r => (r.fees || 0) === 0 },
    { label: 'Processing fee (Stripe)', get: (r) => `− ${money(r.stripeFees)}`, muted: true, hideIfZero: r => (r.stripeFees || 0) === 0 },
    { label: 'Platform fee (FodyFee)', get: (r) => `− ${money(r.platformFees)}`, muted: true, hideIfZero: r => (r.platformFees || 0) === 0 },
    { label: 'Net Sales', get: (r) => money(r.net), bold: true },
    { label: 'Orders', get: (r) => r.orders },
    { label: 'Items Sold', get: (r) => r.items },
    { label: 'Average Ticket', get: (r) => money(r.avgTicket) },
    { label: 'Canceled (excluded)', get: (r) => r.canceled, muted: true, hideIfZero: true },
    { section: 'Payment Breakdown' },
    { label: 'Cash', get: (r) => `${r.cash.orders} orders · ${money(r.cash.amount)}` },
    { label: 'Card', get: (r) => `${r.card.orders} orders · ${money(r.card.amount)}` },
    { label: 'Open / Pay-later', get: (r) => `${r.open.orders} orders · ${money(r.open.amount)}`, muted: true, hideIfZero: r => r.open.orders === 0 },
    { label: 'Other', get: (r) => `${r.other.orders} orders · ${money(r.other.amount)}`, muted: true, hideIfZero: r => r.other.orders === 0 },
    { section: 'Tips' },
    { label: 'Total Tips', get: (r) => money(r.tips), bold: true },
    { label: 'Cash tips', get: (r) => money(r.cashTips), muted: true },
    { label: 'Card tips', get: (r) => money(r.cardTips), muted: true },
    { section: 'By Channel' },
    ...['POS', 'Kiosk', 'Online', 'Other'].map((ch) => ({
      label: ch,
      get: (r) => r.channels[ch] ? `${r.channels[ch].orders} orders · ${money(r.channels[ch].amount)}` : '—',
      muted: false,
    })),
  ];

  const renderMultiTable = () => (
    <div style={{ overflowX: 'auto' }}>
      <table className="table table-sm" style={{ fontSize: 13, marginBottom: 0 }}>
        <thead>
          <tr>
            <th style={{ borderTop: 'none', minWidth: 140 }}></th>
            {perLocation.map(({ loc }) => (
              <th key={loc.id} style={{ borderTop: 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {loc.companyName || loc.locationName || 'Location'}
              </th>
            ))}
            <th style={{ borderTop: 'none', textAlign: 'right', color: '#f97316', fontWeight: 800, whiteSpace: 'nowrap' }}>
              TOTAL
            </th>
          </tr>
        </thead>
        <tbody>
          {METRIC_ROWS.map((row, idx) => {
            if (row.section) {
              return (
                <tr key={`s-${idx}`}>
                  <td colSpan={perLocation.length + 1} style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: '#9ca3af', paddingTop: 14, borderBottom: 'none' }}>
                    {row.section}
                  </td>
                </tr>
              );
            }
            // hideIfZero: hide the row when EVERY column is zero
            if (row.hideIfZero) {
              const allZero = perLocation.every(({ report }) => (typeof row.hideIfZero === 'function' ? row.hideIfZero(report) : report.canceled === 0)) && (typeof row.hideIfZero === 'function' ? row.hideIfZero(combined) : combined.canceled === 0);
              if (allZero) return null;
            }
            return (
              <tr key={`r-${idx}`}>
                <td style={{ color: row.muted ? '#6b7280' : '#1f2937', fontWeight: row.bold ? 800 : 500 }}>{row.label}</td>
                {perLocation.map(({ loc, report }) => (
                  <td key={loc.id} style={{ textAlign: 'right', color: row.muted ? '#6b7280' : '#111827', fontWeight: row.bold ? 800 : 600, whiteSpace: 'nowrap' }}>
                    {row.get(report)}
                  </td>
                ))}
                <td style={{ textAlign: 'right', color: '#f97316', fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {row.get(combined)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="card mb-4 day-close-report" style={{ border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
      <style>{`@media print { body * { visibility: hidden; } .day-close-report, .day-close-report * { visibility: visible; } .day-close-report { position: absolute; left: 0; top: 0; width: 100%; border: none; } .day-close-no-print { display: none !important; } }`}</style>
      {/* Header */}
      <div style={{ background: '#1f2937', color: '#fff', padding: '18px 24px' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#f97316', fontWeight: 800 }}>Day Close Report</div>
            <h4 className="mb-0 font-weight-bold" style={{ color: '#fff' }}>{locName}</h4>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>{dateLabel}</div>
          </div>
          <div className="d-flex align-items-center day-close-no-print" style={{ gap: 8 }}>
            <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={date} onChange={(e) => setDate(e.target.value)} />
            <button className="btn btn-sm btn-outline-light" onClick={load} disabled={loading}>
              <i className="fas fa-rotate"></i>
            </button>
            <button className="btn btn-sm btn-warning" style={{ background: '#f97316', borderColor: '#f97316', color: '#fff' }} onClick={() => window.print()}>
              <i className="fas fa-print mr-1"></i>Print
            </button>
          </div>
        </div>
      </div>

      <div className="card-body" style={{ padding: '8px 24px 20px' }}>
        {error && <div className="alert alert-danger mt-3">{error}</div>}
        {loading ? (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: '#f97316' }} /></div>
        ) : isMulti ? (
          <>
            {renderMultiTable()}
            <div className="text-center mt-3" style={{ fontSize: 11, color: '#9ca3af' }}>
              Generated {generatedAt} · FodyFee · {locName}
            </div>
          </>
        ) : (
          <>
            {/* Sales summary */}
            <SectionTitle>Sales Summary</SectionTitle>
            <Row label="Gross Sales" value={money(combined.gross)} />
            <Row label="Discounts" value={`− ${money(combined.discounts)}`} muted />
            <Row label="Refunds" value={`− ${money(combined.refunds)}`} muted />
            {combined.fees > 0 && <Row label="Processing fees (collected)" value={`− ${money(combined.fees)}`} muted />}
            {combined.stripeFees > 0 && <Row label="Processing fee (Stripe)" value={`− ${money(combined.stripeFees)}`} muted />}
            {combined.platformFees > 0 && <Row label="Platform fee (FodyFee)" value={`− ${money(combined.platformFees)}`} muted />}
            <Row label="Net Sales" value={money(combined.net)} bold />
            <Row label="Orders" value={combined.orders} />
            <Row label="Items Sold" value={combined.items} />
            <Row label="Average Ticket" value={money(combined.avgTicket)} />
            {combined.canceled > 0 && <Row label={`Canceled orders (excluded)`} value={combined.canceled} muted />}

            {/* Payments */}
            <SectionTitle>Payment Breakdown</SectionTitle>
            <Row label="Cash" value={`${combined.cash.orders} orders · ${money(combined.cash.amount)}`} />
            <Row label="Card" value={`${combined.card.orders} orders · ${money(combined.card.amount)}`} />
            {combined.open.orders > 0 && <Row label="Open / Pay-later" value={`${combined.open.orders} orders · ${money(combined.open.amount)}`} muted />}
            {combined.other.orders > 0 && <Row label="Other" value={`${combined.other.orders} orders · ${money(combined.other.amount)}`} muted />}

            {/* Tips */}
            <SectionTitle>Tips</SectionTitle>
            <Row label="Total Tips" value={money(combined.tips)} bold />
            <Row label="Cash tips" value={money(combined.cashTips)} indent muted />
            <Row label="Card tips" value={money(combined.cardTips)} indent muted />

            {/* Channels */}
            <SectionTitle>By Channel</SectionTitle>
            {['POS', 'Kiosk', 'Online', 'Other'].filter((c) => combined.channels[c]).map((c) => (
              <Row key={c} label={c} value={`${combined.channels[c].orders} orders · ${money(combined.channels[c].amount)}`} />
            ))}

            <div className="text-center mt-3" style={{ fontSize: 11, color: '#9ca3af' }}>
              Generated {generatedAt} · FodyFee · {locName}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DayCloseReport;
